import React from 'react';
import { Box, CircularProgress, IconButton, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Text } from '../Typography';
import IpfsDocumentSigner from './IpfsDocumentSigner';
import PdfPreview from './PdfPreview';
import TemplateVarsForm, { humanizeToken } from './TemplateVarsForm';
import { useIpfsDocumentCreator } from './useIpfsDocumentCreator';
import useMetaMask from '../../contexts/useMetaMask';
import { DOCUMENT_TYPES, DocumentType, IpfsDocument, SigningMethod } from './ipfsDocumentTypes';
import masterAgreementPdf from '../../assets/legal_docs/master-agreement.pdf';
import patronIdentityPdf from '../../assets/legal_docs/patron-identity.pdf';
import noticeOfChallengePdf from '../../assets/legal_docs/notice-of-challenge.pdf';
import verifierEngagementPdf from '../../assets/legal_docs/verifier-engagement.pdf';
import operatingAgreementPdf from '../../assets/legal_docs/operating-agreement.pdf';
import {
  Body,
  ControlsColumn,
  Details,
  DocumentLabel,
  FieldHint,
  FieldLabel,
  FieldWrapper,
  HeaderSection,
  HeaderSubtitle,
  HeaderTitle,
  OutlinedButton,
  PreviewColumn,
  PrimaryButton,
  ProcessingButton,
  Section,
  SignatureHeading,
  VarTextField,
} from './signerStyles';

// Static template previews we ship as bundled assets. Types without an entry (e.g. the newer
// covenant/deed types) have no local preview — the create form falls back to a placeholder.
const DOCUMENT_TYPE_PDF: Partial<Record<DocumentType, string>> = {
  [DocumentType.MASTER_ARCHITECTURE]: masterAgreementPdf,
  [DocumentType.PATRON_IDENTITY]: patronIdentityPdf,
  [DocumentType.NOTICE_OF_CHALLENGE]: noticeOfChallengePdf,
  [DocumentType.VERIFIER_ENGAGEMENT]: verifierEngagementPdf,
  [DocumentType.OPERATING_AGREEMENT]: operatingAgreementPdf,
};

const ErrorNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ backgroundColor: '#ffebee', padding: '12px 16px', borderRadius: '8px', mt: 2 }}>
    <Text small sx={{ color: '#c62828', wordBreak: 'break-word' }}>
      {children}
    </Text>
  </Box>
);

export interface IpfsDocumentCreatorProps {
  /** Fixed document type. When provided, the type is locked and not user-selectable. */
  documentType?: DocumentType;
  /** Fixed additional signers (excluding the creator, who is always signer 1). When provided, the signer list is locked. */
  signatories?: string[];
  /** Forwarded to the signer view: fires once when the connected wallet signs the created document. */
  onSigned?: (document: IpfsDocument) => void;
  /** Pre-filled template variable values, keyed by placeholder token (e.g. `TOKEN_ID`). */
  initialVars?: Record<string, string>;
  /** Placeholder keys that are pre-filled by the caller and should be disabled. */
  lockedVarKeys?: string[];
  /**
   * Hold creation until every template field has a value. For a document the creator signs alone:
   * their signature is the last one, and the backend only finalizes on a signature, so a field
   * left blank would leave the document signed but never finalized.
   */
  requireAllFields?: boolean;
}

/**
 * Admin-facing component for creating a new IPFS document: choose the document type, optionally
 * pre-fill template fields, and declare the required signers. Once created it transitions straight
 * into the full signing view (IpfsDocumentSigner) for the new document, so the creator can fill
 * remaining fields and add their own signature without a reload.
 */
const IpfsDocumentCreator: React.FC<IpfsDocumentCreatorProps> = ({
  documentType,
  signatories,
  onSigned,
  initialVars,
  lockedVarKeys = [],
  requireAllFields = false,
}) => {
  const {
    type,
    setType,
    alwaysWetInk,
    placeholders,
    loadingPlaceholders,
    placeholdersError,
    varDrafts,
    setVarDraft,
    creatorAddress,
    signers,
    signerMethods,
    setSigner,
    setSignerMethod,
    addSigner,
    removeSigner,
    createLoading,
    createError,
    createdDocId,
    handleCreate,
  } = useIpfsDocumentCreator({ initialType: documentType, initialSigners: signatories, initialVars });

  const { currentAccount } = useMetaMask();

  // When the caller supplies these, they're fixed — the user only fills template fields and signs.
  const lockType = documentType !== undefined;
  const lockSigners = signatories !== undefined;

  const unfilledCount = placeholders.filter((key) => !(varDrafts[key] ?? '').trim()).length;
  const createBlocked = requireAllFields && (loadingPlaceholders || placeholders.length === 0 || unfilledCount > 0);

  // Once the document exists, hand off to the signer view for the rest of the lifecycle.
  if (createdDocId !== null) {
    return <IpfsDocumentSigner documentId={createdDocId} onSigned={onSigned} />;
  }

  return (
    <Details>
      <HeaderSection>
        <HeaderTitle>Create a Document</HeaderTitle>
        <HeaderSubtitle>
          Generate a new legal document from a template, declare its required signers, and pre-fill
          any fields you are responsible for. Signers complete their own fields and sign with their
          wallets.
        </HeaderSubtitle>
      </HeaderSection>

      <Body horizontal={false}>
        <PreviewColumn>
          <DocumentLabel>{humanizeToken(type)} — Template Preview</DocumentLabel>
          {DOCUMENT_TYPE_PDF[type] ? (
            <PdfPreview fileUrl={DOCUMENT_TYPE_PDF[type]!} />
          ) : (
            <Box
              sx={{
                height: 720,
                borderRadius: '20px',
                border: '1px dashed #ddd',
                backgroundColor: '#fafafa',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 1,
                padding: 3,
              }}
            >
              <Text sx={{ fontWeight: 700, color: '#555' }}>{humanizeToken(type)}</Text>
              <Text small sx={{ color: '#999', maxWidth: 320 }}>
                No local template preview for this document type. The real document is generated on
                the server — create it, then use the draft preview in the signing view.
              </Text>
            </Box>
          )}
        </PreviewColumn>

        <ControlsColumn>
          <Section>
            <SignatureHeading>Document Type</SignatureHeading>
            <FieldWrapper>
              {lockType ? (
                <VarTextField value={humanizeToken(type)} disabled size="small" fullWidth />
              ) : (
                <VarTextField
                  select
                  value={type}
                  onChange={(e) => setType(e.target.value as typeof type)}
                  size="small"
                  fullWidth
                >
                  {DOCUMENT_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {humanizeToken(t)}
                    </MenuItem>
                  ))}
                </VarTextField>
              )}
            </FieldWrapper>
            {placeholdersError && <ErrorNote>{placeholdersError}</ErrorNote>}
          </Section>

          <Section>
            <SignatureHeading>Required Signers</SignatureHeading>
            <Text small sx={{ color: '#666', mt: -1, mb: 2, lineHeight: '18px' }}>
              {alwaysWetInk ? (
                <>
                  This document type is <strong>always wet-ink</strong> — every signer signs on
                  paper. You are not automatically a signer; add each required signer below. A signer
                  may be any identifier, including an off-platform party without an account (e.g.
                  "landowner-jane-doe"). To download/upload scans yourself, add your own wallet as one
                  of the signers.
                </>
              ) : (
                <>
                  Wallet signers must belong to a verified platform user. Wet-ink signers may be any
                  identifier — even an off-platform party without an account (e.g. a nominal
                  placeholder like "landowner-jane-doe"), who signs on paper and has their scan
                  uploaded.
                </>
              )}
            </Text>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Creator is signer 1 only for non-always-wet-ink types. */}
              {!alwaysWetInk && (
                <FieldWrapper>
                  <FieldLabel>
                    Signer 1
                    <LockOutlinedIcon sx={{ fontSize: 14, color: '#999' }} />
                  </FieldLabel>
                  <VarTextField
                    value={creatorAddress || 'Connect your wallet'}
                    disabled
                    size="small"
                    fullWidth
                  />
                  <FieldHint>You (the document creator) are always the first signer.</FieldHint>
                </FieldWrapper>
              )}
              {signers.map((signer, index) => {
                // Always-wet-ink types force every signer to WET_INK (no per-signer selector).
                const method = alwaysWetInk ? SigningMethod.WET_INK : signerMethods[index] ?? SigningMethod.WALLET;
                const signerNumber = index + (alwaysWetInk ? 1 : 2);
                return (
                  <FieldWrapper key={index}>
                    <FieldLabel>
                      Signer {signerNumber}
                      {lockSigners && <LockOutlinedIcon sx={{ fontSize: 14, color: '#999' }} />}
                    </FieldLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VarTextField
                        value={signer}
                        onChange={(e) => setSigner(index, e.target.value)}
                        placeholder={
                          method === SigningMethod.WET_INK ? 'wallet or off-platform identifier' : '0x wallet address'
                        }
                        size="small"
                        fullWidth
                        disabled={lockSigners}
                      />
                      {alwaysWetInk ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            minWidth: 130,
                            flexShrink: 0,
                            justifyContent: 'center',
                            height: 40,
                            borderRadius: '12px',
                            border: '1px solid #e0e0e0',
                            backgroundColor: '#f4f4f4',
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 12,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            color: '#555',
                          }}
                        >
                          <LockOutlinedIcon sx={{ fontSize: 13, color: '#999' }} />
                          Wet-Ink
                        </Box>
                      ) : (
                        <VarTextField
                          select
                          value={method}
                          onChange={(e) => setSignerMethod(index, e.target.value as SigningMethod)}
                          size="small"
                          disabled={lockSigners}
                          sx={{ minWidth: 130, flexShrink: 0 }}
                        >
                          <MenuItem value={SigningMethod.WALLET}>Wallet</MenuItem>
                          <MenuItem value={SigningMethod.WET_INK}>Wet-Ink</MenuItem>
                        </VarTextField>
                      )}
                      {!lockSigners && (
                        <IconButton
                          aria-label="Remove signer"
                          onClick={() => removeSigner(index)}
                          size="small"
                          sx={{ color: '#999' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </FieldWrapper>
                );
              })}
            </Box>
            {!lockSigners && (
              <OutlinedButton
                onClick={addSigner}
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
              >
                Add Signer
              </OutlinedButton>
            )}
          </Section>

          <TemplateVarsForm
            heading="Document Fields"
            description="Optionally fill any fields you are responsible for now. Signers can complete the rest before finalization."
            placeholders={placeholders}
            values={varDrafts}
            // Nothing is saved yet, so what's typed is what counts toward "still need a value".
            templateVars={varDrafts}
            currentAccount={currentAccount}
            canEdit
            loading={loadingPlaceholders}
            onChange={setVarDraft}
            lockedKeys={lockedVarKeys}
          />

          <Section>
            {createLoading ? (
              <ProcessingButton disabled variant="contained">
                <CircularProgress size={20} sx={{ color: 'white' }} />
              </ProcessingButton>
            ) : (
              <PrimaryButton onClick={handleCreate} disabled={createBlocked} variant="contained">
                Create Document
              </PrimaryButton>
            )}
            {createBlocked && unfilledCount > 0 && (
              <Text small sx={{ color: '#888', fontSize: '12px', mt: 1.5 }}>
                Fill in every field first — your signature completes this document.
              </Text>
            )}
            {createError && <ErrorNote>{createError}</ErrorNote>}
          </Section>
        </ControlsColumn>
      </Body>
    </Details>
  );
};

export default IpfsDocumentCreator;
